// Pure parsing helpers for the Smart Objects "Import data" modal
// (SmartObjectImportModal.jsx). No React, no dependencies — the xlsx package
// is lazy-imported by the modal itself, never here.
//
// The pipeline is: raw text / worksheet → GRID (string[][]) → columns + rows
// shaped for the data-table recipe draft (columns: string[], rows: string[][]).

/**
 * Minimal RFC-4180-ish delimited-text parser. Handles:
 *   - quoted fields ("a,b")
 *   - escaped quotes inside quoted fields ("say ""hi""")
 *   - delimiters and newlines INSIDE quoted fields
 *   - \r\n / \n / \r line endings
 *   - a quote appearing mid-field of an UNQUOTED cell (kept literally)
 * Never throws; always returns string[][].
 */
export function parseDelimited(text, delimiter = ',') {
  const s = String(text ?? '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let fieldStarted = false; // true once the current field has any content/quote

  const endField = () => {
    row.push(field);
    field = '';
    fieldStarted = false;
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') { // escaped quote
          field += '"';
          i += 1;
        } else {
          inQuotes = false; // closing quote; trailing junk (rare) stays literal
        }
      } else {
        field += ch; // delimiters/newlines inside quotes are data
      }
      continue;
    }
    if (ch === '"' && !fieldStarted) {
      inQuotes = true;
      fieldStarted = true;
      continue;
    }
    if (ch === delimiter) {
      endField();
      continue;
    }
    if (ch === '\r') {
      if (s[i + 1] === '\n') i += 1; // swallow the \n of \r\n
      endRow();
      continue;
    }
    if (ch === '\n') {
      endRow();
      continue;
    }
    field += ch;
    fieldStarted = true;
  }
  // Flush the trailing field/row (no newline at EOF). A file ending in a
  // newline leaves field='' and row=[] — nothing to flush, no phantom row.
  if (fieldStarted || field !== '' || row.length > 0) endRow();
  return rows;
}

/** Full CSV parse (comma-delimited). */
export function parseCsv(text) {
  return parseDelimited(text, ',');
}

/**
 * Tidy a raw parsed grid for the preview/import:
 *   - every cell → trimmed string (NBSP → space)
 *   - fully-empty rows dropped
 *   - trailing all-empty columns dropped (spreadsheets love those)
 */
export function cleanGrid(grid) {
  if (!Array.isArray(grid)) return [];
  const rows = grid
    .map((row) => (Array.isArray(row) ? row : [row]).map((cell) => {
      if (cell === null || cell === undefined) return '';
      return String(cell).replace(/\u00a0/g, ' ').trim();
    }))
    .filter((row) => row.some((cell) => cell !== ''));
  // Drop trailing columns that are empty in EVERY row.
  let width = rows.reduce((w, r) => Math.max(w, r.length), 0);
  while (width > 0 && rows.every((r) => !(r[width - 1] ?? '').length)) width -= 1;
  return rows.map((r) => r.slice(0, width));
}

/**
 * Parse text pasted from Excel / Google Sheets (or hand-typed CSV) into a
 * clean grid. Spreadsheet clipboard grids are tab-separated — if any tab is
 * present we treat the paste as TSV (quotes still honored, multi-line cells
 * survive); otherwise we fall back to full CSV parsing.
 */
export function parseClipboardGrid(text) {
  const s = String(text ?? '');
  if (!s.trim()) return [];
  return cleanGrid(parseDelimited(s, s.includes('\t') ? '\t' : ','));
}

/**
 * Grid → { columns, rows } shaped for the data-table draft.
 *   - firstRowHeaders: first grid row becomes the column names (blanks become
 *     "Column N"); otherwise names are synthesized and every row is data.
 *   - maxCols/maxRows: recipe caps (field.maxItems); overflow is truncated
 *     and reported via truncatedCols/truncatedRows so the UI can warn.
 *   - every data row is padded/truncated to exactly columns.length cells.
 * Returns null for an empty grid.
 */
export function gridToTableData(grid, { firstRowHeaders = true, maxCols = Infinity, maxRows = Infinity } = {}) {
  const clean = cleanGrid(grid);
  if (clean.length === 0) return null;

  const naturalWidth = clean.reduce((w, r) => Math.max(w, r.length), 0);
  const capCols = Number.isFinite(maxCols) && maxCols > 0 ? maxCols : Infinity;
  const width = Math.min(naturalWidth, capCols);

  let columns;
  let body;
  if (firstRowHeaders) {
    const head = clean[0];
    columns = Array.from({ length: width }, (_, i) => (head[i] ?? '').trim() || `Column ${i + 1}`);
    body = clean.slice(1);
  } else {
    columns = Array.from({ length: width }, (_, i) => `Column ${i + 1}`);
    body = clean;
  }

  const capRows = Number.isFinite(maxRows) && maxRows > 0 ? maxRows : Infinity;
  const truncatedRows = Math.max(0, body.length - capRows);
  const rows = body
    .slice(0, capRows)
    .map((r) => Array.from({ length: width }, (_, i) => String(r[i] ?? '')));

  // The table recipe requires at least one data row — importing a lone header
  // line still yields a usable (empty) first row instead of an Apply error.
  if (rows.length === 0) rows.push(Array.from({ length: width }, () => ''));

  return {
    columns,
    rows,
    truncatedCols: Math.max(0, naturalWidth - width),
    truncatedRows,
  };
}

/**
 * True when a recipe schema is the data-table shape this importer supports:
 * BOTH a "columns" and a "rows" field of type list.
 */
export function schemaSupportsTableImport(schema) {
  if (!Array.isArray(schema)) return false;
  const has = (key) => schema.some((f) => f?.key === key && f?.type === 'list');
  return has('columns') && has('rows');
}
